export type ViewFieldKind =
	| 'text'
	| 'jaws'
	| 'vise'
	| 'ops'
	| 'checkbox'
	| 'length'
	| 'weight'
	| 'number'
	| 'numberArray';

export const JAW_OPTIONS = Array.from(
	{ length: 15 },
	(_, i) => `${i + 1} lbs. Jaws`
);

export const VISE_OPTIONS = [1, 2];

export const OP_COUNT_OPTIONS = [1, 2];

/** Only toggle sections of the VSC's own UI; kept in the JSON, never shown. */
export const HIDDEN_PARAMETER_KEYS = new Set([
	'showAdvancedPick',
	'showAdvancedOp1',
	'showAdvancedOp2',
	'showAdvancedTransfer',
	'showAdvancedPlace',
]);

/**
 * Same as HIDDEN_PARAMETER_KEYS, for keys at the top of the JSON. `jaws` is
 * kept in sync from the Op 1/Op 2 Jaws selects and the Operations select.
 */
export const HIDDEN_ROOT_KEYS = new Set(['process', 'jaws']);

export interface ViewField {
	label: string;
	/** Candidate keys, first present wins: 1- and 3-op programs rename a few. */
	keys: string[];
	kind: ViewFieldKind;
	/** Read from the top of the JSON instead of `parameters`. */
	root?: boolean;
	/** Only shown while every one of these `parameters` checkboxes is on. */
	showWhen?: string[];
	/** Hidden while the program's `op_count` is below this. */
	minOps?: number;
}

export interface ViewSection {
	title?: string;
	rows: ViewField[][];
	/** Hidden while the program's `op_count` is below this. */
	minOps?: number;
}

const text = (label: string, ...keys: string[]): ViewField => ({ label, keys, kind: 'text' });
const jaws = (label: string, ...keys: string[]): ViewField => ({ label, keys, kind: 'jaws' });
const vise = (label: string, ...keys: string[]): ViewField => ({ label, keys, kind: 'vise' });
const ops = (label: string, ...keys: string[]): ViewField => ({ label, keys, kind: 'ops' });
const check = (label: string, ...keys: string[]): ViewField => ({ label, keys, kind: 'checkbox' });
const length = (label: string, ...keys: string[]): ViewField => ({ label, keys, kind: 'length' });
const weight = (label: string, ...keys: string[]): ViewField => ({ label, keys, kind: 'weight' });
const number = (label: string, ...keys: string[]): ViewField => ({ label, keys, kind: 'number' });
const numberArray = (label: string, ...keys: string[]): ViewField => ({
	label,
	keys,
	kind: 'numberArray',
});
const root = (field: ViewField): ViewField => ({ ...field, root: true });
const when = (showWhen: string[], field: ViewField): ViewField => ({
	...field,
	showWhen,
});
const forMinOps = (minOps: number, field: ViewField): ViewField => ({
	...field,
	minOps,
});

// Mirrors the part editor on the VersaBuilt controller, so a program reads the
// same here as it does at the machine.
export const VIEW_LAYOUT: ViewSection[] = [
	{
		rows: [
			[root(ops('Number of Setups', 'op_count'))],
			[
				root(text('Part Number', 'name')),
				root(text('Part Description', 'description')),
			],
		],
	},
	{
		title: 'Stock',
		rows: [
			[
				length('Stock Width', 'width'),
				length('Stock Length', 'length'),
				length('Stock Height', 'partHeight'),
				weight('Stock Weight', 'preOp1Weight', 'preOpWeight'),
			],
		],
	},
	{
		title: 'Pick Options',
		rows: [
			[
				length('Pick Height', 'pickHeight'),
				number('Tune Pick Tilt Angle', 'tunePickTilt'),
				number('Op 1 Robot Settle Angle', 'op1SettleAngle'),
			],
			[
				length('Infeed X Offset', 'infeedXOffset'),
				length('Infeed Y Offset', 'infeedYOffset'),
			],
			[
				check('Use Part Detection', 'partDetection'),
				check('Enable Part Find on Pick', 'findPart'),
				check('Clap Gripper on Pick', 'clapPick'),
				check('Op 1 Robot Settle', 'op1RobotSettle'),
				check('Custom Infeed Spacing', 'customInfeedSpacing'),
			],
		],
	},
	{
		title: 'Op 1 Options',
		rows: [
			[
				jaws('Op 1 Jaws', 'op1Jaws', 'op1JawId'),
				vise('Op 1 Vise', 'op1Vise', 'op1ViseLoad'),
				number('Op 1 Pressure', 'op1Pressure'),
			],
			[length('Part Detection Tolerance', 'partDetectionTolerance')],
			[check('Y Push', 'op1YPush')],
			[
				when(['op1YPush'], length('Y Push Height Offset', 'yPushHeight')),
				when(['op1YPush'], length('Y Push X Offset', 'op1YPushXOffset')),
				when(
					['op1YPush', 'op1YPushOverride'],
					length('Y Push Distance', 'op1YPushDistance')
				),
			],
			[when(['op1YPush'], check('Override Y Push Distance', 'op1YPushOverride'))],
			[check('Op 1 Vise Settle', 'op1ViseSettle')],
			[
				text('Op 1 CNC Program', 'op1Program'),
				text('Vise 1 CNC Table Load Program', 'tableLoad'),
				text('Vise 2 CNC Table Load Program', 'tableLoadVise2'),
				text('CNC Vise Wash Program', 'washProgram'),
			],
		],
	},
	{
		title: 'Part Transfer Options',
		minOps: 2,
		rows: [
			[
				length('Op 1 to 2 Transfer Height', 'op1to2TransferHeight'),
				length('After Op 1 Part Height', 'transferPartHeight'),
				length('Op 1 to 2 Clearance Height', 'op1to2ClearanceHeight'),
				weight('Post Op 1 Part Weight', 'postOp1Weight'),
			],
			[
				length('Part Find Starting Height', 'op1to2FindPartClearanceHeight'),
				length('X Vise Transfer Offset', 'xViseTransferOffset', 'xViseTransferOffsetOp2'),
				length('Y Vise Transfer Offset', 'yViseTransferOffset', 'yViseTransferOffsetOp2'),
			],
			[
				check('Enable Part Find on Transfer', 'findPartOnTransfer'),
				check('Calibrate Transfer Position', 'useCalibratedTransfer'),
				check('Always Unload Op1 Jaws', 'unloadOp1Jaws'),
				check('CNC Vise Wash after Op Transfer', 'washAfterOpTransfer'),
				check('Run Table Load after Op Transfer', 'tableLoadAfterOpXfer'),
			],
			[when(['useCalibratedTransfer'], numberArray('Above Transfer', 'aboveTransfer'))],
			[
				when(
					['useCalibratedTransfer'],
					numberArray('Calibrated Transfer', 'calibratedTransfer')
				),
			],
			[
				text('CNC Vise Wash Program after Op Transfer', 'washProgramAfterOpTransfer'),
				text('CNC Table Load Program After Op Transfer', 'tableLoadProgramAfterOpXfer'),
			],
		],
	},
	{
		title: 'Op 2 Options',
		minOps: 2,
		rows: [
			[
				jaws('Op 2 Jaws', 'op2Jaws', 'op2JawId'),
				vise('Op 2 Vise', 'op2Vise', 'op2ViseLoad'),
				weight('Post Op 2 Part Weight', 'postOp2Weight'),
			],
			[
				check('OP 2 Robot Settle', 'op2RobotSettle'),
				check('Op 2 Vise Settle', 'op2ViseSettle'),
				number('Op 2 Settle Angle', 'op2SettleAngle'),
			],
			[number('Op 2 Pressure', 'op2Pressure'), text('Op 2 CNC Program', 'op2Program')],
		],
	},
	{
		title: 'Place Options',
		rows: [
			[
				length('Place Height', 'placeHeight'),
				length('Finished Part Height', 'finishedPartHeight'),
			],
			[
				length('Outfeed X Offset', 'outfeedXOffset'),
				length('Outfeed Y Offset', 'outfeedYOffset'),
			],
			[
				check('Clap Gripper on Place', 'clapPlace'),
				check('Enable Part Find on Place', 'findPartPlace'),
				check('Bin Drop', 'binDrop'),
			],
		],
	},
	{
		title: 'Jaw Cleaning Options',
		rows: [
			[
				check('Op 1 Empty Jaws Cleaning', 'emptyJawsBlowOff'),
				check('90 Degree Jaw Wash for Op 1', 'wash90degOp1'),
				check('Universal Jaw Wash for Op 1', 'op1UniversalWash'),
			],
			[
				forMinOps(2, check('Op 2 Part Cleaning', 'op2PartBlowOff')),
				forMinOps(2, check('Op2 Empty Jaws Cleaning', 'op2EmptyJawsBlowOff')),
				forMinOps(
					2,
					check('Op2 Empty Jaws Blow Off After Wash', 'op2EmptyJawsBlowOffAfterWash')
				),
				forMinOps(2, check('90 Degree Jaw Wash for Op 2', 'wash90degOp2')),
				forMinOps(2, check('Universal Jaw Wash for Op 2', 'op2UniversalWash')),
			],
			[
				check('Dump Coolant at In CNC Home', 'dumpCoolantAtInCNCHome'),
				check('Dump Coolant at CNC Home', 'dumpCoolantAtCNCHome'),
			],
		],
	},
	{
		title: 'VersaWash Settings',
		rows: [
			[
				number('Cycles', 'versaWashCycles'),
				number('Dunk Speed', 'versaWashDunkSpeed'),
				number('Drip Time(seconds)', 'versaWashDripTime'),
				number('Rotational Drip Speed', 'versaWashRotationalDripSpeed'),
			],
		],
	},
];
